<template>
  <div class="active" data-testid="screen-active">
    <section class="left" :style="{ '--items-scale': itemsScale }">
      <div class="doc-type">{{ docTypeLabel }}</div>
      <div class="plate-row">
        <div class="plate" data-testid="plate">
          <span>{{ doc.plate.characters }}</span>
          <span>{{ doc.plate.number }}</span>
        </div>
        <div class="province-line">
          <div class="province" data-testid="province">{{ doc.plate.province }}</div>
          <div v-if="doc.carType" class="car-type" data-testid="car-type">{{ doc.carType }}</div>
        </div>
      </div>
      <div class="contact">
        <span class="phone" data-testid="phone">{{ phone }}</span>
        <span v-if="doc.ownerName" class="owner" data-testid="owner">
          <span class="owner-label">เจ้าของรถ</span> {{ doc.ownerName }}
        </span>
      </div>

      <ul class="items" data-testid="items">
        <li v-for="(item, index) in doc.items" :key="index" class="item">
          <span class="item-name">{{ item.name }}</span>
          <span class="item-price">{{ formatMoney(item.price) }}</span>
          <span v-if="item.discount" class="item-discount">ส่วนลด</span>
          <span v-if="item.discount" class="item-price item-discount">-{{ formatMoney(Math.abs(item.discount)) }}</span>
        </li>
      </ul>

      <dl v-if="showBreakdown" class="breakdown">
        <dt>รวม</dt>
        <dd>{{ formatMoney(doc.totals.subtotal) }}</dd>
        <template v-if="doc.totals.discount">
          <dt>ส่วนลด</dt>
          <dd>-{{ formatMoney(Math.abs(doc.totals.discount)) }}</dd>
        </template>
        <template v-if="doc.totals.vat">
          <dt>ภาษีมูลค่าเพิ่ม</dt>
          <dd>{{ formatMoney(doc.totals.vat) }}</dd>
        </template>
      </dl>

      <InlineRatingSection
        v-if="showInlineRating"
        ref="inlineRatingRef"
        :rating-session="ratingSession"
        @rated="$emit('rated', $event)"
        @unavailable="$emit('unavailable', $event)"
        @network-error="$emit('network-error', $event)"
      />
    </section>

    <aside class="right">
      <BranchLogo :branch="branch" />
      <div class="total">
        <div class="total-label">ยอดรวมทั้งสิ้น</div>
        <div class="total-value" data-testid="total">{{ formatMoney(doc.totals.net) }}</div>
        <div class="total-unit">บาท</div>
      </div>
      <template v-if="payment">
        <div class="qr-block">
          <div v-if="payment.qr" class="qr-caption" data-testid="qr-caption">สแกนเพื่อชำระเงิน</div>
          <PaymentQr :qr="payment.qr" />
        </div>
        <div class="account" data-testid="account">
          <div v-if="payment.accountName" class="account-name">{{ payment.accountName }}</div>
          <div v-if="payment.bankName" class="account-bank">{{ payment.bankName }}</div>
        </div>
      </template>
      <div
        v-if="documentStatus"
        class="document-status"
        :class="'status-' + documentStatus.color"
        :data-label="documentStatus.label"
        data-testid="document-status"
        role="status"
      >
        <span class="status-content">
          <i class="mdi status-icon" :class="statusIcon" data-testid="document-status-icon" aria-hidden="true"></i>
          <span class="status-label">{{ documentStatus.label }}</span>
        </span>
      </div>
    </aside>
  </div>
</template>

<script>
import BranchLogo from './BranchLogo.vue';
import InlineRatingSection from './InlineRatingSection.vue';
import PaymentQr from './PaymentQr.vue';
import { isInlineRatingAvailable } from '@shared/customer-display/protocol';
import { DOCUMENT_STATUSES, sanitizeDocumentStatus } from '@shared/customer-display/documentStatus';
import { DOC_TYPE_LABELS, formatMoney, formatPhone } from './format';

// Presentation only; labels, colors and status selection remain shared.
const STATUS_ICONS = {
  'awaiting-save': 'mdi-content-save-outline',
  pending: 'mdi-clock-outline',
  processing: 'mdi-progress-clock',
  'awaiting-payment': 'mdi-cash-clock',
  paid: 'mdi-check-circle-outline',
  refunded: 'mdi-cash-refund',
  cancelled: 'mdi-close-circle-outline'
};

export default {
  name: 'ActiveScreen',
  components: { BranchLogo, InlineRatingSection, PaymentQr },
  props: {
    doc: { type: Object, required: true },
    payment: { type: Object, default: null },
    branch: { type: Object, default: null },
    ratingSession: { type: Object, default: null }
  },
  computed: {
    documentStatus() {
      return DOCUMENT_STATUSES[sanitizeDocumentStatus(this.doc.status)] || null;
    },
    statusIcon() {
      return STATUS_ICONS[sanitizeDocumentStatus(this.doc.status)] || '';
    },
    docTypeLabel() {
      return DOC_TYPE_LABELS[this.doc.docType] || '';
    },
    phone() {
      return formatPhone(this.doc.phone);
    },
    showBreakdown() {
      return Boolean(this.doc.totals.discount || this.doc.totals.vat);
    },
    // Pre-payment widget: available while the session is still 'waiting'
    // before payment (no expiry yet - see protocol.js's
    // isInlineRatingAvailable), or already 'rated' so the confirmed state
    // survives a page reload.
    showInlineRating() {
      return isInlineRatingAvailable(this.ratingSession) || (this.ratingSession && this.ratingSession.status === 'rated');
    },
    // Shrinks the items table (font-size + row padding, via --items-scale
    // on .items/.item below) once the inline rating widget is also
    // competing for the same column's vertical space and there are enough
    // items for that to matter -- this column has no scrollbar by design
    // (kiosk display, no pointer), so a long item list would otherwise be
    // silently clipped by `.left`'s overflow:hidden once InlineRatingSection
    // appears. A count-based heuristic, not a real layout measurement
    // (ResizeObserver): this display is already built on vh/vw-relative
    // sizing throughout for exactly this "roughly fits any kiosk screen"
    // reason, so this follows the same approach rather than introducing a
    // new measurement mechanism for just this one case.
    itemsScale() {
      if (!this.showInlineRating) return 1;
      const count = this.doc.items.length;
      if (count <= 4) return 1;
      if (count <= 6) return 0.85;
      if (count <= 8) return 0.72;
      if (count <= 11) return 0.6;
      return 0.5;
    }
  },
  methods: {
    formatMoney,
    // Phase 6 hardware-remote routing (CustomerDisplayPage.vue): forwards to
    // the real InlineRatingSection instance's own submitFromHelper (which
    // calls the exact same onSelect() a tap uses), or null when the widget
    // isn't actually mounted right now (showInlineRating false, or the
    // ref hasn't been set yet) -- the caller then reports 'unavailable'
    // rather than guessing. No rating/session/backend logic lives here.
    submitFromHelper(rating) {
      if (!this.showInlineRating || !this.$refs.inlineRatingRef) return null;
      return this.$refs.inlineRatingRef.submitFromHelper(rating);
    }
  }
};
</script>

<style scoped>
/* Type scale (px): 52 plate / 48 total | 30 province | 24 phone, owner | 22 items,
   prices | 15-18 labels, unit, account, branch name | 18 scan caption. */
.active {
  display: grid;
  grid-template-columns: 7fr 3fr;
  height: 100vh;
  overflow: hidden;
}
.left {
  background: #f3f4f6;
  padding: 2.5vh 3vw;
  /* Extra bottom padding reserves room for SettlementFooter, which is a
     position:fixed sibling (CustomerDisplayPage.vue) overlaid on top of
     this column, not part of its normal flow -- without this, the inline
     rating prompt (the last thing in this column) can end up hidden behind
     the footer bar whenever a settlement is published at the same time. */
  padding-bottom: max(9vh, calc(var(--settlement-height, 0px) + 1vh));
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.doc-type {
  font-size: calc(17px * var(--font-scale));
  color: #6b7280;
}
.plate-row {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin: 0.4rem 0;
  flex-wrap: wrap;
}
.plate {
  display: inline-flex;
  gap: 1rem;
  padding: 0.2rem 1.6rem;
  font-size: calc(52px * var(--font-scale));
  font-weight: 600;
  line-height: 1.25;
  background: #fff;
  border: 4px solid #1f2937;
  border-radius: 1rem;
}
.province {
  font-size: calc(30px * var(--font-scale));
  font-weight: 500;
}
.province-line {
  display: flex;
  align-items: baseline;
  gap: 1rem;
  min-width: 0;
}
.car-type {
  font-size: calc(15px * var(--font-scale));
  font-weight: 400;
  color: #000;
  min-width: 0;
  overflow-wrap: anywhere;
}
.contact {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.8rem 1.5rem;
  margin: 1.2rem 0;
}
.phone,
.owner {
  padding: 0.4rem 1.4rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.75rem;
}
.owner {
  font-size: calc(26.4px * var(--font-scale));
  color: #374151;
  overflow-wrap: anywhere;
}
.owner-label {
  font-size: calc(17px * var(--font-scale));
  color: #6b7280;
  margin-right: 0.5rem;
}
.phone {
  font-size: calc(26.4px * var(--font-scale));
  color: #374151;
  letter-spacing: 0.04em;
}
.items {
  list-style: none;
  padding: 0;
  margin: 1.5vh 0 0;
  flex: 1;
  overflow: hidden;
  /* --items-scale (set on .left, see ActiveScreen.vue's itemsScale
     computed) shrinks the whole items table once there are many rows AND
     the inline rating widget is also showing, so both stay visible without
     scrolling in this no-scrollbar kiosk layout. 1 (unscaled) the rest of
     the time. */
  font-size: calc(22px * var(--font-scale) * var(--items-scale, 1));
}
.item {
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 2rem;
  padding: calc(1vh * var(--items-scale, 1)) 0;
  border-bottom: 1px solid #d1d5db;
}
.item-name {
  min-width: 0;
  overflow-wrap: anywhere;
}
.items .item-price {
  font-variant-numeric: tabular-nums;
  text-align: right;
  font-weight: 500;
}
.item-discount {
  color: #b91c1c;
  font-size: calc(22px * var(--font-scale) * var(--items-scale, 1));
}
.breakdown {
  display: grid;
  grid-template-columns: 1fr auto;
  margin: 1vh 0 0;
  font-size: calc(18px * var(--font-scale));
  color: #374151;
}
.breakdown dd {
  margin: 0;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.right {
  display: flex;
  flex-direction: column;
  gap: 2vh;
  align-items: stretch;
  padding: 2.5vh 2vw;
  /* A wrapped settlement can exceed the default reserve on a short kiosk. */
  padding-bottom: max(9vh, calc(var(--settlement-height, 0px) + 1vh));
  background: #fff;
  border-left: 1px solid #e8eef5;
  overflow: hidden;
  min-width: 0;
}
.total {
  text-align: center;
}
.total-label {
  font-size: calc(18px * var(--font-scale));
  color: #4c1d95;
}
.total-value {
  font-size: calc(48px * var(--font-scale));
  line-height: 1.15;
  font-weight: 700;
  color: #4c1d95;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.total-unit {
  font-size: calc(18px * var(--font-scale));
  line-height: 1.2;
  color: #4c1d95;
}
.document-status {
  --status-gap: 0.5rem;
  --status-previous-font-size: 18px;
  --status-previous-icon-size: 22.5px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: 'status';
  align-items: center;
  justify-content: center;
  gap: var(--status-gap);
  width: 100%;
  margin-top: auto;
  padding: 0.6rem 0.7rem;
  border: 1px solid #aaafb6;
  background: transparent;
  border-radius: 0.75rem;
  text-align: center;
  font-size: calc(16px * var(--font-scale));
  font-weight: 600;
  /* Keep the previous line box height as the lettering becomes quieter. */
  line-height: calc(25.2px * var(--font-scale));
  flex-shrink: 0;
}
/* Reserve the original text wrapping and icon space without rendering a
   duplicate label. Smaller lettering must not resize the status or QR area. */
.document-status::before {
  content: attr(data-label);
  visibility: hidden;
  grid-area: status;
  min-width: 0;
  padding-inline-start: calc(var(--status-previous-icon-size) * var(--font-scale) + var(--status-gap));
  font-size: calc(var(--status-previous-font-size) * var(--font-scale));
  font-weight: 700;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.status-content {
  grid-area: status;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: inherit;
  min-width: 0;
}
.status-icon {
  flex-shrink: 0;
  font-size: 1.2em;
  line-height: 1;
}
.status-label {
  min-width: 0;
  overflow-wrap: anywhere;
}
.status-gray { color: #64748b; }
.status-blue { color: #526b89; }
.status-orange { color: #96651d; }
.status-green { color: #34765e; }
.status-red { color: #a34f58; }
/* The caption sits against the QR itself. The QR box has its own white margin (0.6rem
   padding + the image at 90% of the box), so the caption is pulled down by that much. */
.qr-block {
  display: flex;
  flex-direction: column;
  padding-top: 20px;
  flex: 1;
  min-height: 0;
}
/* Fit the QR inside the height left by total/status/branding/account. Its
   image retains object-fit:contain, so a short kiosk never crops the code. */
.qr-block ::v-deep .qr {
  flex: 1;
  min-height: 0;
  aspect-ratio: auto;
  grid-template-rows: minmax(0, 1fr);
  grid-template-columns: minmax(0, 1fr);
}
.qr-caption {
  text-align: center;
  font-size: calc(18px * var(--font-scale));
  line-height: 1.2;
  font-weight: 700;
  color: #000;
  position: relative;
  z-index: 1;
  margin-bottom: calc(-0.6rem - 4.5%);
  flex-shrink: 0;
}
.account {
  text-align: center;
  flex-shrink: 0;
}
.account-name {
  font-size: calc(16px * var(--font-scale));
  font-weight: 500;
}
.account-bank {
  font-size: calc(15px * var(--font-scale));
  color: #6b7280;
}
/* Keep payment details and the longest status readable on short kiosks. */
@media (max-height: 650px) {
  .right { gap: 1vh; }
  .right ::v-deep .logo { max-height: 9vh; }
  .qr-block { padding-top: 4px; }
  .document-status {
    --status-gap: 0.4rem;
    --status-previous-font-size: 16px;
    --status-previous-icon-size: 20px;
    padding: 0.4rem 0.5rem;
    font-size: calc(14px * var(--font-scale));
    line-height: calc(22.4px * var(--font-scale));
  }
}
</style>
